import { MessageType } from '@protobuf-ts/runtime'
import { RpcOptions, RpcTransport, ServiceInfo, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { AllowedRefs } from 'lib/websocket-notify'

export interface ListableRequest {
    pageSize: number
    pageToken: string
    refs?: Array<AllowedRefs>
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type ListableResponse = {
    list: Array<any>
    nextPageToken: string
    expanded?: Array<any>
}
export type InferUnaryResponse<T> = T extends (...args) => UnaryCall<any, infer R> ? R : never
export type InferListUnaryResponse<T> = T extends (...args) => UnaryCall<any, infer R>
    ? R extends ListableResponse
    ? R
    : never
    : never

export type InferUnaryResponseListType<T> = T extends (...args) => UnaryCall<any, infer R>
    ? R extends ListableResponse
    ? R['list']
    : never
    : never

export type InferRequest<T> = T extends (arg: infer A, options?: RpcOptions) => UnaryCall<any, any>
    ? A extends object
    ? A
    : never
    : never
export type InferListRequest<T> = T extends (arg: infer A, options?: RpcOptions) => UnaryCall<any, any>
    ? A extends ListableRequest
    ? A
    : never
    : never
export type GRPCClient<C extends ServiceInfo> = new (transport: RpcTransport) => C
export type AnyMessage = MessageType<any>
export type ListMessage = MessageType<ListableRequest>
export type UnaryMethod<Req extends object> = (input: Req, options?: RpcOptions) => UnaryCall<Req, any>
/* eslint-enable @typescript-eslint/no-explicit-any */

export const compositeKey = (reducer: string, cacheKey: string): string => `${reducer} 🌮🌮 ${cacheKey}`

export const equal = (x: Set<string>, y: Set<string>): boolean => x.size === y.size && [...x].every((v) => y.has(v))
export const omitEmpty = (o: object): object => {
    const n = {}
    for (const [k, v] of Object.entries(o)) {
        if (!v) {
            continue
        }
        if (Array.isArray(v)) {
            if (v.length) {
                n[k] = v
            }
            continue
        }
        if (typeof v !== 'object') {
            n[k] = v
            continue
        }
        const newV = omitEmpty(v)
        if (Object.entries(newV).length === 0) {
            continue
        }
        n[k] = newV
    }
    return n
}

export const serializeQuery = <Args extends object | null>(k: string, args: Args): string =>
    `${k} 🌮🌮 ${args ? JSON.stringify(omitEmpty(args)) : ''}`

// Underlying issue to track: https://github.com/vercel/next.js/issues/59594
//
// Oringally for the "namespace" of our redux stores, we using the Class.name of the GRPC client.
//
// Next.js / SWC in production builds, sometimes, optimizes away the value of Class.name.
//
// In theory, you could set jsc.keepClassNames to true in your swc config, but that doesn't work with Next.js:
// <https://swc.rs/docs/configuration/compilation#jsckeepclassnames>
//
// This is because... Next.js is mean, and doesn't let you set .swcrc options.
//
// So, instead we instantiate the GRPC client, and then get the typeName from the instance.
//
// This is "relateively" free, because the constructors in generated code, just set the property _transport.
//
const clientTypeNameCache = new WeakMap<GRPCClient<ServiceInfo>, string>()
export const clientTypeName = (ClientConstructor: GRPCClient<ServiceInfo>): string => {
    const rv = clientTypeNameCache.get(ClientConstructor)
    if (rv) {
        return rv
    }
    const typeName = (new ClientConstructor({} as RpcTransport)).typeName
    clientTypeNameCache.set(ClientConstructor, typeName)
    return typeName
}
